// ChatContext.js
import React, { createContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { statsAPI } from './api';

// Support dynamic environment-based URLs or fallback to production
const SOCKET_SERVER_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:4000' 
  : 'https://test3-0ar4.onrender.com';

export const ChatContext = createContext();

export function ChatProvider({ children, currentUser }) {
  const socketRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [onlineCount, setOnlineCount] = useState(null);
  const [displayOnlineCount, setDisplayOnlineCount] = useState(Math.floor(Math.random() * 5) + 5);

  useEffect(() => {
    const saved = localStorage.getItem('chatMessages');
    if (saved) setChatMessages(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem('chatMessages', JSON.stringify(chatMessages));
  }, [chatMessages]);

  useEffect(() => {
    socketRef.current = io(SOCKET_SERVER_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: { token: localStorage.getItem('token') }, // Pass JWT for handshake auth
    });

    socketRef.current.on('connect', () => {
      console.log('Chat connected');
    });

    // Online count (if backend emits it)
    const onlineHandler = (data) => {
      const count =
        typeof data === 'number' ? data :
        (data && typeof data.count === 'number' ? data.count : null);
      if (count !== null) setOnlineCount(count);
    };
    socketRef.current.on('online_players_update', onlineHandler);
    socketRef.current.on('total_online_update', onlineHandler); // Real-time global count
    socketRef.current.on('online_count', onlineHandler);
    socketRef.current.on('onlineCount', onlineHandler);

    socketRef.current.on('chatHistory', (messages) => {
      setChatMessages(messages);
    });

    socketRef.current.on('chatMessage', (msg) => {
      setChatMessages((prev) => {
        if (prev.find((m) => m.id === msg.id)) return prev;
        return [...prev, msg].slice(-100);
      });
    });

    socketRef.current.on('chatError', (err) => {
      if (window._addToast) {
        window._addToast({
          type: 'error',
          title: 'Chat Error',
          message: err
        });
      }
    });

    socketRef.current.on('chat_clear', () => {
      setChatMessages([]);
      localStorage.removeItem('chatMessages');
    });

    socketRef.current.on('chat_delete', ({ messageId }) => {
      setChatMessages((prev) => prev.filter((m) => m.id !== messageId && m._id !== messageId));
    });

    socketRef.current.on('maintenance_status', (data) => {
      window.dispatchEvent(new CustomEvent('maintenanceStatus', { detail: data }));
    });

    socketRef.current.on('announcement_update', (data) => {
      window.dispatchEvent(new CustomEvent('announcementUpdate', { detail: data }));
    });

    socketRef.current.on('force_logout', (data) => {
      window.dispatchEvent(new CustomEvent('forceLogout', { detail: data }));
    });

    socketRef.current.on('notification', (data) => {
      window.dispatchEvent(new CustomEvent('notification', { detail: data }));
    });

    // Listen for balance updates
    socketRef.current.on('balance_update', (data) => {
      window.dispatchEvent(new CustomEvent('balanceUpdate', { detail: data }));
    });

    // Listen for new tips
    socketRef.current.on('new_tip', (data) => {
      window.dispatchEvent(new CustomEvent('newTip', { detail: data }));
    });

    // Listen for high wins
    socketRef.current.on('high_win', (data) => {
      window.dispatchEvent(new CustomEvent('highWin', { detail: data }));
    });

    // Listen for level up events
    socketRef.current.on('level_up', (data) => {
      window.dispatchEvent(new CustomEvent('levelUp', { detail: data }));
    });

    return () => {
      socketRef.current?.off('online_players_update', onlineHandler);
      socketRef.current?.off('online_count', onlineHandler);
      socketRef.current?.off('onlineCount', onlineHandler);
      socketRef.current?.disconnect();
    };
  }, []);

  // Fallback polling for online count (uses existing API endpoint).
  useEffect(() => {
    let alive = true;
    let intervalId = null;

    const poll = async () => {
      try {
        const res = await statsAPI.online();
        const count = res?.data?.count;
        if (!alive) return;
        if (typeof count === 'number') setOnlineCount(count);
      } catch {
        // Endpoint may require auth; avoid showing fake numbers.
        if (alive) setOnlineCount(null);
      }
    };

    poll();
    intervalId = setInterval(poll, 10000);

    return () => {
      alive = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // Lifelike behavior: jitter the display count so it looks "real"
  useEffect(() => {
    if (onlineCount !== null) setDisplayOnlineCount(onlineCount);
  }, [onlineCount]);


  useEffect(() => {
    const handleAuth = () => {
      if (currentUser && socketRef.current) {
        socketRef.current.emit('authenticate', { userId: currentUser.id || currentUser._id });
      }
    };
    
    if (socketRef.current) {
      if (socketRef.current.connected) handleAuth();
      socketRef.current.on('connect', handleAuth);
    }

    return () => {
      if (socketRef.current) socketRef.current.off('connect', handleAuth);
    };
  }, [currentUser]);

  const sendMessage = (text, replyTo = null) => {
    if (!text.trim() || !socketRef.current || !currentUser) return;

    const newMsg = {
      id: Date.now(),
      user: currentUser.username,
      text: text.trim(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      ...(replyTo ? { replyTo: { user: replyTo.user, text: replyTo.text?.slice(0, 80) } } : {}),
    };

    socketRef.current.emit('chatMessage', newMsg);
  };

  return (
    <ChatContext.Provider value={{ chatMessages, sendMessage, socket: socketRef.current, onlineCount: displayOnlineCount }}>
      {children}
    </ChatContext.Provider>
  );
}
